// @ts-strict-ignore
import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Button, FieldLabel, FlexBox } from "@braze/beacon-core";
import { InputField } from "@braze/beacon-forms";
import { yupResolver } from "@braze/hookform-resolvers-legacy/yup";
import { useI18nLocale } from "@braze/i18n-core";
import * as Yup from "@braze/yup-legacy";

import sharedConstants from "@lib/shared_constants.json";

import useI18nNamespace from "@services/useI18nNamespace";

import { SelectField } from "@components/ReactHookForm";

import useDepartments from "@scenes/AccountSettings/AccountProfile/useDepartments";

import { isAllowedToContactSalesDomain } from "../WorkEmailInputField/isRestrictedEmail";
import { FormHeaderText } from "../FormHeaderText";
import { PhoneNumberInputField } from "../PhoneNumberInputField";
import { WorkEmailInputField } from "../WorkEmailInputField";
import { workEmailSchema } from "../WorkEmailInputField";
import { phoneNumberCountryCodeSchema, phoneNumberSchema, restrictedCountryCodes } from "../utils";
import { StyledForm } from "./ProspectDetails.style";
import type { ProspectDetailsProps, ProspectDetailsType } from "./types";

const SALES_BANNER_DELAY = 2000;

export const ProspectDetails = ({ prospectDetails, onSubmit, setShowSalesBanner }: ProspectDetailsProps) => {
  const { t } = useI18nNamespace("free-trial-registration");
  const currentLocale = useI18nLocale();
  const departments = useDepartments();

  const validationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string()
          .required(t("first-name-required-error-text", "First name is required"))
          .max(
            sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            t("first-name-max-error-text", "First name must be at most {maxParamLength, number} characters", {
              maxParamLength: sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            })
          ),
        lastName: Yup.string()
          .required(t("last-name-required-error-text", "Last name is required"))
          .max(
            sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            t("last-name-max-error-text", "Last name must be at most {maxParamLength, number} characters", {
              maxParamLength: sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            })
          ),
        email: workEmailSchema(t),
        phoneNumber: phoneNumberSchema(t),
        phoneNumberCountry: phoneNumberCountryCodeSchema(t),
        jobTitle: Yup.string()
          .required(t("job-title-required-error-text", "Job title is required"))
          .max(
            sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            t("job-title-max-error-text", "Job title must be at most {maxParamLength, number} characters", {
              maxParamLength: sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            })
          ),
        company: Yup.string()
          .required(t("company-required-error-text", "Company is required"))
          .max(
            sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            t("company-max-error-text", "Company must be at most {maxParamLength, number} characters", {
              maxParamLength: sharedConstants.FREE_TRIAL.PARAM_LENGTH_LIMIT,
            })
          )
          .test(
            "invalid_characters",
            t(
              "invalid-company-name",
              "Company name can't include certain symbols: less than (<), greater than (>), equals (=), semicolon (;), or quotation marks (\")"
            ),
            (companyName) => {
              return !companyName.match(/[="<>;]/);
            }
          )
          .test(
            "valid_normalized",
            t(
              "valid-normalized-company-name",
              "Company name must include at least one Latin character (A-Z, a-z) or number (0-9)"
            ),
            (companyName) => {
              return !!companyName.match(/[a-zA-Z0-9]/);
            }
          ),
        department: Yup.string().required(t("department-required-error-text", "Department is required")),
      }),
    [t]
  );

  const form = useForm<ProspectDetailsType>({
    defaultValues: prospectDetails,
    shouldFocusError: true,
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  });

  const {
    control,
    handleSubmit,
    getFieldState,
    formState: { errors },
    watch,
  } = form;

  const email = watch("email");
  const phoneNumberCountry = watch("phoneNumberCountry");
  const emailFieldState = getFieldState("email");

  // For Japanese locale, show last name before first name
  const isJapaneseLocale = currentLocale === "ja";

  const firstNameField = (
    <InputField
      control={control}
      id="firstName"
      name="firstName"
      label={t("first-name", "First name")}
      placeholder={t("first-name-placeholder", "Enter your first name")}
      fullWidth
    />
  );

  const lastNameField = (
    <InputField
      control={control}
      id="lastName"
      name="lastName"
      label={t("last-name", "Last name")}
      placeholder={t("last-name-placeholder", "Enter your last name")}
      fullWidth
    />
  );

  useEffect(() => {
    if ((email && emailFieldState.isTouched) || phoneNumberCountry) {
      const showBanner =
        isAllowedToContactSalesDomain(email) || restrictedCountryCodes.includes(phoneNumberCountry);
      const timeout = setTimeout(() => setShowSalesBanner(showBanner), showBanner ? SALES_BANNER_DELAY : 0);

      return () => clearTimeout(timeout);
    }
  }, [email, emailFieldState, phoneNumberCountry, setShowSalesBanner]);

  return (
    <StyledForm data-cy="prospectDetailsForm" id="prospectDetailsForm" onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...form}>
        <FormHeaderText />
        <FlexBox direction="column">
          {isJapaneseLocale ? (
            <>
              {lastNameField}
              {firstNameField}
            </>
          ) : (
            <>
              {firstNameField}
              {lastNameField}
            </>
          )}
          <WorkEmailInputField label={t("work-email-label", "Work email")} name="email" control={control} />
          <PhoneNumberInputField
            label={t("phone-number-label", "Mobile phone number")}
            phoneNumberFieldName="phoneNumber"
            phoneNumberCountryFieldName="phoneNumberCountry"
            control={control}
          />
          <InputField
            control={control}
            id="jobTitle"
            name="jobTitle"
            label={t("job-title", "Job title")}
            placeholder={t("job-title-placeholder", "Enter your job title")}
            fullWidth
          />
          <InputField
            control={control}
            id="company"
            name="company"
            label={t("company", "Company")}
            placeholder={t("company-placeholder", "Type in your company name")}
            fullWidth
          />
          <FieldLabel
            label={t("department", "Department")}
            htmlFor="department"
            status="danger"
            statusText={errors.department?.message}
          >
            <SelectField
              id="department"
              name="department"
              control={control}
              options={departments}
              isClearable={false}
              size="lg"
              fluid
              placeholder={t("department-selection-label", "Select a department")}
              maxMenuHeight={200}
            />
          </FieldLabel>
          <FlexBox>
            <Button size="full" type="submit">
              {t("prospect-details-continue-button-text", "Continue")}
            </Button>
          </FlexBox>
        </FlexBox>
      </FormProvider>
    </StyledForm>
  );
};
